import RangeSteps from '@scripts/components/RangeSteps';
import FormPartial from '@scripts/forms/FormPartial';
import CvRiskHelpers from '@scripts/helpers';

/**
 * "Profile" form partial
 */
export default class extends FormPartial {
  /**
   * Patient gender
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #gender;

  /**
   * Patient age
   *
   * @private
   *
   * @type {HTMLSelectElement}
   */
  #age;

  /**
   * List of countries by risk (not exhaustive)
   *
   * @private
   *
   * @type {object}
   */
  #risks;

  /**
   * Patient country of first nationality
   *
   * @private
   *
   * @type {HTMLSelectElement}
   */
  #country;

  /**
   * Risk of Patient country of first nationality
   *
   * @private
   *
   * @type {RangeSteps}
   */
  #countryRisk;

  /**
   * Patient ethnicity
   *
   * @private
   *
   * @type {HTMLSelectElement}
   */
  #ethnicity;

  /**
   * Ethnicity field wrapper
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #ethnicityWrapper;

  constructor(form) {
    super(form);

    this.#gender = this._form.element.querySelector('#cvriskProfileGender');
    this.#age = this._form.element.querySelector('#cvriskProfileAge');
    this.#risks = window?.CvRisk?.countriesRisk || {};
    this.#country = this._form.element.querySelector('#cvriskProfileCountry');
    this.#countryRisk = new RangeSteps(
      this._form.element.querySelector('#cvriskCountryRisk')
    );
    this.#ethnicity = this._form.element.querySelector(
      '#cvriskProfileEthnicity'
    );
    this.#ethnicityWrapper = this._form.element.querySelector(
      '#cvriskProfileEthnicityWrapper'
    );
  }

  /**
   * If the ethnicity field is required for the selected country
   *
   * @private
   *
   * @return {bool}
   */
  #isEthnicityRequired() {
    return (
      this.#country.value &&
      window?.CvRisk?.countriesEthnicity &&
      window.CvRisk.countriesEthnicity.indexOf(this.#country.value) > -1
    );
  }

  /**
   * @private
   *
   * @param {string} country Country code
   *
   * @return {void}
   */
  #setCountryRisk() {
    let code = this.#country.value;
    let risk = '';
    for (let r in this.#risks) {
      let arr = this.#risks[r];
      let match = arr.find((c) => c.toUpperCase() === code.toUpperCase());
      if (match) {
        risk = r;
        break;
      }
    }

    if (risk) {
      this.#countryRisk.setRangeValueFromDatalist(risk);
    } else {
      this.#countryRisk.reset();
    }
  }

  /**
   * Show/Hide ethnicity wrapper
   *
   * @private
   *
   * @return {void}
   */
  #toggleEthnicity() {
    if (this.#ethnicityWrapper) {
      if (this.#isEthnicityRequired()) {
        this.#ethnicityWrapper.classList.add('cvrisk-active');
      } else {
        this.#ethnicityWrapper.classList.remove('cvrisk-active');
      }
    }
  }

  /**
   * @private
   *
   * @return {void}
   */
  #handlerCountryChange() {
    this.#setCountryRisk();
    this.#toggleEthnicity();
  }

  initialize() {
    this.#country.addEventListener('change', () =>
      this.#handlerCountryChange()
    );
    this.#toggleEthnicity();
  }

  validate() {
    this._resetMessages();

    let errors = [];

    // Gender
    if (!this.#gender.querySelector('input[type="radio"]:checked')) {
      errors.push(CvRiskHelpers.error('gender_required'));
    }

    // Age
    if (!this.#age.value) {
      errors.push(CvRiskHelpers.error('age_required'));
    }

    // Country
    if (!this.#country.value) {
      errors.push(CvRiskHelpers.error('country_required'));
    }

    // Country risk
    if (!this.#countryRisk.getValue()) {
      errors.push(CvRiskHelpers.error('country_risk_required'));
    }

    // Ethnicity
    if (!this.#ethnicity.value && this.#isEthnicityRequired()) {
      errors.push(CvRiskHelpers.error('ethnicity_required'));
    }

    this.addErrors(errors);

    return super.validate();
  }

  reset() {
    super.reset();

    // Gender
    this.#gender
      .querySelectorAll('input[type="radio"]:checked')
      .forEach((input) => {
        input.checked = false;
      });

    // Age
    this.#age.value = this.#age.querySelector('option').value;

    // Country
    this.#country.value = this.#country.querySelector('option').value;

    // Country risk
    this.#countryRisk.reset();

    // Ethnicity
    this.#ethnicity.value = this.#ethnicity.querySelector('option').value;
  }
}
